import { useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { karkhanaTitleFor } from "../karkhanaPaths";

/** The query with some keys set or removed (null/undefined/"" removes). Returns "?a=1" or "". */
export const withParams = (search: string, patch: Record<string, string | null | undefined>): string => {
  const p = new URLSearchParams(search);
  Object.entries(patch).forEach(([k, v]) => (v === null || v === undefined || v === "" ? p.delete(k) : p.set(k, v)));
  const s = p.toString();
  return s ? `?${s}` : "";
};

const OVERLAY = "khOverlay";
/** The query of the entry under the overlay, to tell whether Back would undo other changes. */
const OVERLAY_BASE = "khOverlayBase";

/**
 * One piece of screen state kept in the address (?key=…), so Back, reload and a shared link reopen
 * the same view. Changing it replaces the history entry: typing in a search box never adds entries.
 */
export const useAddressParam = (key: string, fallback = ""): [string, (value: string) => void] => {
  const history = useHistory();
  const location = useLocation();
  const value = new URLSearchParams(location.search).get(key) ?? fallback;
  const set = useCallback(
    (v: string) => {
      const cur = history.location;
      history.replace({ pathname: cur.pathname, search: withParams(cur.search, { [key]: v === fallback ? null : v }), hash: cur.hash, state: cur.state });
    },
    [history, key, fallback]
  );
  return [value, set];
};

/**
 * Something that covers the page (a drawer, a sheet): ?key=id. Opening adds a history entry so a
 * phone's Back closes it instead of leaving the page; moving to the next one replaces it; closing
 * goes back when this page opened it, otherwise drops the key.
 */
export const useAddressOverlay = (key: string) => {
  const history = useHistory();
  const location = useLocation();
  const value = new URLSearchParams(location.search).get(key);
  const to = (id: string | null) => {
    const cur = history.location;
    return { pathname: cur.pathname, search: withParams(cur.search, { [key]: id }), hash: cur.hash };
  };
  const open = (id: string) =>
    history.push({
      ...to(id),
      state: { ...((history.location.state as any) || {}), [OVERLAY]: key, [OVERLAY_BASE]: withParams(history.location.search, { [key]: null }) },
    });
  const switchTo = (id: string) => history.replace({ ...to(id), state: history.location.state });
  const close = () => {
    const state = (history.location.state as any) || {};
    // Back only when it lands on the same query minus the overlay; if something else changed while
    // it was open (a date), drop the key in place so that change is kept.
    if (state[OVERLAY] === key && state[OVERLAY_BASE] === to(null).search) history.goBack();
    else {
      const { [OVERLAY]: _o, [OVERLAY_BASE]: _b, ...rest } = state;
      history.replace({ ...to(null), state: Object.keys(rest).length ? rest : undefined });
    }
  };
  return { value, open, switchTo, close };
};

/**
 * The browser tab names the screen: "Rounds · Floor · Karkhana". KarkhanaFrame sets the page's own
 * name; a detail page passes its record ("Saree 18301") once it has loaded.
 */
export const useKarkhanaTitle = (screen?: string | null) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (screen) document.title = karkhanaTitleFor(pathname, screen);
  }, [pathname, screen]);
};
